import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronLeft, ChevronRight, Star, Target, TrendingUp, Trophy, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';
import { sampleTipsters } from '@/data/sampleData';

export function TipsterSpotlight() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const tipsters = sampleTipsters.filter(t => t.isPremium);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % tipsters.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, tipsters.length]);

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % tipsters.length);
  };

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + tipsters.length) % tipsters.length);
  };

  const currentTipster = tipsters[currentIndex];

  const getCountryFlag = (code: string) => {
    const flags: Record<string, string> = {
      'BR': '🇧🇷',
      'ES': '🇪🇸',
      'UK': '🇬🇧',
      'US': '🇺🇸',
      'IT': '🇮🇹',
      'CA': '🇨🇦',
    };
    return flags[code] || '🌍';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-500" />
            Tipster Spotlight
          </h2>
          <p className="text-sm text-muted-foreground">Featured expert tipsters</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPrev}
            className="h-8 w-8"
            aria-label="Previous tipster"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={goToNext}
            className="h-8 w-8"
            aria-label="Next tipster"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 border-primary/20 hover:shadow-2xl transition-all duration-500">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 opacity-50" />
        
        <CardContent className="relative z-10 p-8">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Avatar Section */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-full blur-2xl animate-pulse" />
              <Avatar className="relative h-32 w-32 ring-4 ring-primary/30">
                <AvatarImage src={currentTipster.avatar} alt={currentTipster.name} />
                <AvatarFallback className="text-2xl bg-primary/10">
                  {currentTipster.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              
              {currentTipster.streak >= 5 && (
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-full p-2 animate-bounce">
                  <Flame className="w-5 h-5 text-white" />
                </div>
              )}
              
              {currentTipster.isVerified && (
                <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2">
                  <Trophy className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Info Section */}
            <div className="flex-1 space-y-4 text-center md:text-left">
              <div>
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <h3 className="text-2xl font-bold">{currentTipster.name}</h3>
                  {currentTipster.isPremium && (
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                      VIP
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center justify-center md:justify-start gap-2 text-sm text-muted-foreground">
                  <span className="text-xl">{getCountryFlag(currentTipster.country)}</span>
                  <span>{currentTipster.country}</span>
                  <span>•</span>
                  <span>{currentTipster.totalTips.toLocaleString()} tips</span>
                  {currentTipster.streak > 0 && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1 text-green-600 font-medium">
                        <Flame className="w-4 h-4" />
                        {currentTipster.streak} win streak
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-card/50 rounded-lg p-3 border border-border/50">
                  <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-center">{currentTipster.roi}%</div>
                  <div className="text-xs text-muted-foreground text-center">ROI</div>
                </div>
                
                <div className="bg-card/50 rounded-lg p-3 border border-border/50">
                  <div className="flex items-center justify-center gap-1 text-primary mb-1">
                    <Target className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-center">{currentTipster.winRate}%</div>
                  <div className="text-xs text-muted-foreground text-center">Win Rate</div>
                </div>
                
                <div className="bg-card/50 rounded-lg p-3 border border-border/50">
                  <div className="flex items-center justify-center gap-1 text-yellow-600 mb-1">
                    <Trophy className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-center">#{currentIndex + 1}</div>
                  <div className="text-xs text-muted-foreground text-center">Ranking</div>
                </div>
              </div>

              {/* CTA */}
              <Button 
                className="w-full md:w-auto bg-gradient-to-r from-primary to-secondary hover:opacity-90"
                asChild
              >
                <Link to="/tipsters">
                  View Full Profile
                </Link>
              </Button>
            </div>
          </div>

          {/* Carousel Indicators */}
          <div className="flex justify-center gap-1 mt-6">
            {tipsters.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
                aria-label={`Go to tipster ${index + 1} of ${tipsters.length}`}
                aria-current={index === currentIndex ? 'true' : undefined}
                className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-full"
              >
                <span
                  aria-hidden="true"
                  className={`block h-2 rounded-full transition-all ${
                    index === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-muted'
                  }`}
                />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
